import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../../components/forms/Input';
import { DatePicker } from '../../components/forms/DatePicker/DatePicker';
import { cn } from '../../utils/cn';
import { usePoliciesStore } from './policiesStore';
import { policiesService } from './policiesService';

const PolicyForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');
  const { createPolicy } = usePoliciesStore();

  const [formData, setFormData] = useState({
    policy_number: '',
    effective_date: '',
    carrier_name: '',
    premium_amount: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savedPolicyId, setSavedPolicyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('/')) {
      const [month, day, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    if (dateString.includes('.')) {
      const [day, month, year] = dateString.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.policy_number.trim()) errors.policy_number = 'Policy number is required.';
    if (!formData.effective_date) errors.effective_date = 'Effective date is required.';
    if (!formData.carrier_name.trim()) errors.carrier_name = 'Carrier name is required.';
    if (!formData.premium_amount) errors.premium_amount = 'Premium amount is required.';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const policy = await createPolicy({
        ...formData,
        effective_date: formatDateForAPI(formData.effective_date),
        quote: quoteId ? Number(quoteId) : null,
      });
      setSavedPolicyId(policy.id);
    } catch {
      setError('Failed to save policy. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!savedPolicyId) return;
    setDownloading(true);
    setError(null);
    try {
      const blob = await policiesService.downloadPDF(savedPolicyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `policy-${formData.policy_number || savedPolicyId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-2 text-sm font-medium"
          style={{ color: 'var(--color-primary-500)' }}
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
          New Policy
        </h1>
        {quoteId && (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Linked to quote #{quoteId}
          </p>
        )}
      </div>

      {error && (
        <div
          className="mb-6 rounded-lg border p-4"
          style={{ backgroundColor: 'var(--color-error-50)', borderColor: 'var(--color-error-500)' }}
        >
          <p style={{ color: 'var(--color-error-700)' }}>{error}</p>
        </div>
      )}

      {savedPolicyId && (
        <div
          className="mb-6 rounded-lg border p-4"
          style={{ backgroundColor: 'var(--color-success-50)', borderColor: 'var(--color-success-500)' }}
        >
          <p style={{ color: 'var(--color-success-700)' }}>
            ✓ Policy saved successfully. You can now download the PDF.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Policy Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Policy Number"
              value={formData.policy_number}
              onChange={(e) => handleChange('policy_number', e.target.value)}
              errorText={formErrors.policy_number}
              placeholder="e.g. POL-2024-001"
              required
              fullWidth
              disabled={!!savedPolicyId}
            />
            <Input
              label="Carrier Name"
              value={formData.carrier_name}
              onChange={(e) => handleChange('carrier_name', e.target.value)}
              errorText={formErrors.carrier_name}
              placeholder="e.g. Acme Insurance Co."
              required
              fullWidth
              disabled={!!savedPolicyId}
            />
            <DatePicker
              label="Effective Date"
              value={formData.effective_date}
              onChange={(date) => handleChange('effective_date', date)}
              errorText={formErrors.effective_date}
              required
              fullWidth
              disabled={!!savedPolicyId}
            />
            <Input
              label="Premium Amount"
              type="number"
              value={formData.premium_amount}
              onChange={(e) => handleChange('premium_amount', e.target.value)}
              errorText={formErrors.premium_amount}
              placeholder="e.g. 5000.00"
              required
              fullWidth
              disabled={!!savedPolicyId}
            />
          </div>
        </div>

        <div className="flex gap-4 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/policies')}
            className="px-6 py-2 rounded-lg border font-medium"
            style={{
              borderColor: 'var(--color-border-medium)',
              color: 'var(--color-text-primary)',
            }}
          >
            {savedPolicyId ? 'Back to Policies' : 'Cancel'}
          </button>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!savedPolicyId || downloading}
            className={cn(
              'px-6 py-2 rounded-lg font-medium text-white transition-opacity',
              (!savedPolicyId || downloading) && 'opacity-40 cursor-not-allowed'
            )}
            style={{ backgroundColor: 'var(--color-neutral-600)' }}
          >
            {downloading ? 'Downloading…' : 'Download PDF'}
          </button>

          {!savedPolicyId && (
            <button
              type="submit"
              disabled={saving}
              className={cn(
                'px-6 py-2 rounded-lg text-white font-medium',
                saving && 'opacity-50 cursor-not-allowed'
              )}
              style={{ backgroundColor: 'var(--color-primary-500)' }}
            >
              {saving ? 'Saving…' : 'Save Policy'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PolicyForm;
